<template>
  <f7-app v-bind="f7params">
    <component :is="activeLayout" v-if="isReady"></component>
    <loading-layout v-else :error="initError"></loading-layout>

    <!--
          NetworkStatusBar mounts once here at app root so it covers every route.
          It uses Teleport internally, so z-index stacking is not an issue.
        -->

    <!--
      The two platform coaches mount at app root so they can appear over any
      route, but they never open on their own: the nudge scheduler
      (useEngagementNudges.js) decides — device, dismissed key, one overlay at
      a time — and `openNudge` opens the one it shows. Each reports its close.
      Gated on isReady so neither stacks over the loading layout.
    -->
    <huawei-battery-coach
      v-if="isReady"
      :opened="openNudge === 'coach-huawei'"
      @closed="completeNudge('coach-huawei')"
    ></huawei-battery-coach>
    <ios-install-coach
      v-if="isReady"
      :opened="openNudge === 'coach-ios'"
      @closed="completeNudge('coach-ios')"
    ></ios-install-coach>
    <!--
      "Install in Chrome" for Samsung Internet and the other Android browsers:
      usePwaInstall's second presenter (rule 7), opened through the same
      install slot of the scheduler or a profile tap; it reports its close
      with the outcome.
    -->
    <install-in-chrome-sheet
      v-if="isReady"
      :opened="chromeHandoffOpen"
      @closed="chromeHandoffClosed"
    ></install-in-chrome-sheet>
  </f7-app>
</template>

<script setup>
import { until, useEventBus } from '@vueuse/core';
import { f7ready } from 'framework7-vue';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import HuaweiBatteryCoach from './components/HuaweiBatteryCoach.vue';
import InstallInChromeSheet from './components/InstallInChromeSheet.vue';
import IosInstallCoach from './components/IosInstallCoach.vue';
import { updatePolicyFor } from './composables/pwaUpdatePolicy.js';
import { useAuthInitialize } from './composables/useAuth';
import { completeNudge, enqueueNudge, useEngagementNudges } from './composables/useEngagementNudges.js';
import { useEnvironment } from './composables/useEnvironment';
import {
  getPrimingState,
  isSafeOriginRoute,
  usePushNotifications,
} from './composables/usePushNotifications.js';
import { usePwa } from './composables/usePwa';
import { usePwaInstall } from './composables/usePwaInstall.js';
import AppLayout from './layouts/AppLayout.vue';
import LoadingLayout from './layouts/LoadingLayout.vue';
import { adapter, isTma } from './platform/index.js';
import appRoutes, { ONBOARDING_ENTRY } from './router/routes.js';
import { clubLandingPath, engagementFor } from '@/clubs/manifest.js';
import { hideMissingImages, isReplayOn } from '@/collector/previewReplay.js';
import {
  authPaused,
  clearHold,
  collectorHoldActive,
  enterAuthHold,
  hasLocalWork,
  isCollectorPath,
  readHold,
  resumeAfterAuth,
} from '@/collector/session.js';
import { initRealtime, teardownRealtime } from '@/realtime/echo.js';
import {
  collectorBootPreempt,
  EAGER_MEMBER_REQUIRED_PREFIXES,
  sellerRoutePreempt,
  supplierPreviewPreempt,
  supplierRoutePreempt,
} from '@/router/bootPreempts.js';
import { fetchUnreadCount } from '@/services/notification.service.js';
import { useCollectorPrewarmStore } from '@/stores/collectorPrewarmStore.js';
import { useAuthStore } from '@/stores/index.js';
import { useNotificationStore } from '@/stores/notificationStore.js';
import { extractDeepLinkRoute } from '@/utils/notificationDeepLink.js';
import { captureReferralFromStartParam } from '@/utils/referral.js';

const { initEnvironment } = useEnvironment();
const { initialize: initAuth } = useAuthInitialize();
const {
  needRefresh,
  offlineReady,
  updateServiceWorker,
  dismissOfflineReady,
  dismissUpdate,
  isReloadBusy,
  isDismissed,
  holdReload,
  applyUpdateWhenIdle,
} = usePwa();

// usePushNotifications() MUST be called inside App.vue <script setup> so that
// `onScopeDispose` binds to the app effect scope (deferred-work 2026-06-03
// item #4). PwaAdapter only exposes the boolean TMA gate.
const {
  initialize: initPush,
  warmPushInit,
  firePushOptIn,
  pushPreflightDecision,
  setPrimingState,
  navigateTo: pushNavigateTo,
} = usePushNotifications();

// Value-moment-triggered priming bus — subscribed below in f7ready. Emitters
// are the screens that complete a real D4 commit step (order-box item save,
// deposit submit) and the 'registration' completion screen (UnderReviewScreen).
// Payload shape: `{ moment: 'orderbox' | 'deposit' | 'registration' | 'profile_status' }`.
const primingTriggerBus = useEventBus('push:trigger-priming');

// Pending-push-route keys — shared with usePushNotifications.js. App.vue is
// the cold-start drain point for routes captured by either the SW
// notificationclick postMessage (Phase-1a-degraded path) or by an
// OneSignal click that landed before the router was ready.
const PENDING_PUSH_ROUTE_GLOBAL = '__POOLIT_PENDING_PUSH_ROUTE';
const PENDING_PUSH_ROUTE_SS_KEY = 'poolit:pending-push-route';

// Listen for PUSH_NAVIGATE messages from the SW (Phase-1a-degraded path).
// The SW posts this when our own notificationclick handler ran (i.e.
// OneSignal SW absent) and a same-origin client was focused. Cross-origin
// messages are rejected; the type discriminator prevents collision with
// other postMessage producers (e.g. workbox-window's controller messages).
function onPushMessage(evt) {
  if (typeof window === 'undefined') return;
  if (evt.origin !== window.location.origin) return;
  const { data } = evt;
  if (!data || typeof data !== 'object') return;
  if (data.type !== 'PUSH_NAVIGATE') return;
  if (!isSafeOriginRoute(data.route)) return;
  f7ready((f7) => {
    const router = f7?.views?.main?.router;
    if (router) {
      router.navigate(data.route, { clearPreviousHistory: false });
    } else {
      // Router not mounted yet — queue for the isReady drain.
      try {
        window[PENDING_PUSH_ROUTE_GLOBAL] = data.route;
      } catch {
        /* noop */
      }
      try {
        sessionStorage.setItem(PENDING_PUSH_ROUTE_SS_KEY, data.route);
      } catch {
        /* noop */
      }
    }
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('message', onPushMessage);
}

const { t, te } = useI18n();

const isReady = ref(false);
const initError = ref(null);

// One queue for the install card, the two coaches and the push soft-ask
// (useEngagementNudges.js): one overlay at a time, never over the update
// dialog, never mid-transition. The push dialogs below and the install card
// are its presenters; the two coaches are opened from `openNudge`.
const { presentInstallCard, chromeHandoffOpen, chromeHandoffClosed } = usePwaInstall();
const { current: currentNudge } = useEngagementNudges({
  isReady,
  isReloadBusy,
  holdReload,
  isCollectorPath: () => isCollectorPath(),
  // The day page itself, where the collector's install offer may come up.
  isCollectorDayPage: () => /^\/collector\/?$/.test(window.location.pathname),
  primingStatus: (userId) => getPrimingState(userId)?.status,
  presenters: { push: presentPushNudge, install: presentInstallCard },
});
const openNudge = computed(() => currentNudge.value?.kind ?? null);
// The coaches are offered once per boot; the scheduler drops them on any other
// device, inside Telegram, and once dismissed or snoozed. The iOS one is kept
// at boot only as the in-app-browser variant ("open this in Safari"); on
// Safari itself it comes through the install slot, after sign-in.
enqueueNudge({ kind: 'coach-huawei', reason: 'boot' });
enqueueNudge({ kind: 'coach-ios', reason: 'boot' });

// Subscribe to 401s synchronously in setup so requests fired before f7ready
// (e.g. from initEnvironment below) cannot drop their unauthorized event.
// The router-navigate side of the handler is the only piece that needs f7;
// defer that with f7ready inside the callback.
const unauthorizedBus = useEventBus('auth:unauthorized');
unauthorizedBus.on(() => {
  // A 401 means the Sanctum session is gone — an involuntary logout. Tear the
  // socket down BEFORE wiping the store (mirroring useAuth.logout()'s order) so
  // no private channel lingers authenticated as the now-invalid identity — this
  // path bypasses logout() and goes straight to clearSession. Best-effort;
  // re-init runs on the next login via the useAuth chokepoint.
  try {
    teardownRealtime();
  } catch (err) {
    console.error('[App] realtime teardown on unauthorized failed:', err);
  }
  // A collector on his day with a day or a queue on the phone is HELD there,
  // not sent to /login/ — a password most collectors do not have (collector-
  // app P2-B, L-D). Decided BEFORE the store is cleared: the hold reads who
  // was signed in. Idempotent across the several 401s of one expired login.
  // The collector's storage is never touched here.
  const held = enterAuthHold();
  // The "install in Chrome" sheet needs his session (its link is a launch
  // token): taken back, no snooze, so it does not sit over the login screen.
  if (chromeHandoffOpen.value) chromeHandoffClosed('unshown');
  useAuthStore().clearSession();
  held
    .catch(() => false)
    .then((stay) => {
      if (stay) return null;
      f7ready((f7) => {
        f7.views.main?.router?.navigate('/login/', { clearPreviousHistory: true });
      });
      return null;
    })
    .catch(() => {
      /* noop */
    });
});

// The collector's queue resumes when a user is signed in again — the
// Telegram re-entry's cookie-bootstrap, or a login on another tab (L-D). Only
// that user's own hold is cleared (collector/session.js).
watch(
  () => useAuthStore().user?.id,
  (userId) => {
    if (userId != null) resumeAfterAuth(userId);
  },
);

const envInitState = initEnvironment();

const f7params = reactive({
  name: 'Poolit',
  id: 'com.poolit.app',
  theme: 'ios',
  routes: appRoutes,
  // Dark theme is TEMPORARILY force-disabled pending a later refactor.
  // `false` (not 'auto') is deliberate: framework7/components/app/app-class.js
  // init() only calls setDarkMode() when darkMode !== undefined, and
  // setDarkMode(false) takes the non-'auto' branch, which calls
  // disableAutoDarkMode() (never attaches the prefers-color-scheme matchMedia
  // listener) and removes `.dark` from <html> unconditionally — so no OS-level
  // dark preference can ever re-add it. This is the single producer of `.dark`
  // in the app (see src/index.html's `color-scheme` meta for the native-control
  // counterpart). Restore to 'auto' to re-enable dark mode.
  darkMode: false,
  rtl: document.documentElement.dir === 'rtl',
  view: {
    stackPages: true,
    iosDynamicNavbar: true,
    browserHistory: true,
    browserHistorySeparator: '',
    browserHistoryInitialMatch: true,
    allowDuplicateUrls: false,
    animate: true,
    url: '/',
    loadInitialPage: true,
    // Disable preload-previous-page + iOS swipe-back at the view level.
    //
    // Why: F7 v9's back.js (line 143-153) calls `router.tabLoad(route.tab,
    // { preload: true })` for the preloaded previous page, but tab.js's
    // tabLoad uses `router.currentPageEl` (the actual current page) to
    // resolve the `#tab-id` selector — not the preloaded page. So when a
    // user lands directly on /wallet/ and F7 preloads / (the parent in
    // history) for swipe-back, F7 ends up activating #home INSIDE the
    // wallet page. Net effect: every direct tab URL renders the home tab.
    //
    // We use routable tabs as a tabbed shell, NOT a navigation stack —
    // swipe-back from /wallet/ to / makes no UX sense (they're siblings,
    // not parent/child). Disabling both flags is the safe, minimal fix
    // verified against framework7/modules/router/back.js + tab.js source.
    preloadPreviousPage: false,
    iosSwipeBack: false,
    // Keep inactive routable-tab content MOUNTED instead of unmounting it on
    // every tab switch (F7 default is `true` — framework7/components/view/view.js:64).
    //
    // Why this is a correctness flag, not a perf tweak: with the default `true`,
    // navigating between two routable tabs runs tabLoad → onTabLoaded → tabRemove
    // → removeTabContent → setTabContent(null) (framework7/modules/router/tab.js:85,91
    // → framework7-vue/components/tab.js:67), which UNMOUNTS the previous tab's Vue
    // subtree mid-flush. When that subtree holds a @tanstack/vue-virtual list, F7's
    // imperative DOM teardown detaches the fragment's anchor nodes before Vue's
    // removeFragment walk reaches them → `Cannot read properties of null (reading
    // 'nextSibling')` (Vue core #5317, an unfixed null-anchor edge case triggered
    // whenever external code mutates DOM Vue owns). The throw interrupts the same
    // flush that was painting the INCOMING tab, so its `fready` guard never flips →
    // the new tab (e.g. /suppliers/feed/) renders permanently empty despite a
    // successful API response. Confirmed on v9.0.4; the old framework7-vue#4149
    // "flag ignored" bug was v7.1.5 and does NOT apply — v9 core gates tabRemove on
    // this param (the ONLY unmount path for loaded tab content).
    //
    // This is a tabbed shell, not a nav stack (see preloadPreviousPage note above),
    // so persisting each tab's component + scroll across switches is the desired UX.
    // View-level, so it applies to BOTH the seller `/` shell and the supplier
    // `/suppliers/` shell (one shared main <f7-view>); both already effectively keep
    // tabs alive after first load via F7's `f7RouterTabLoaded` short-circuit, so this
    // only extends that to the very first transition. Re-shows of a loaded tab
    // short-circuit at tab.js:97 (no re-fetch); `tab:show`/`tab:hide` still fire on
    // the f7-tab element if a future page needs a refresh-on-show hook.
    unloadTabContent: false,
  },
  colors: {
    primary: '#8E5BFE',
    deeppurple: '#31006F',
    blue: '#59A4FA',
    teal: '#23ECF6',
    lightblue: '#00B8C4',
    green: '#10B981',
    yellow: '#F59E0B',
    red: '#EF4444',
    purple: '#A278FF',
    pink: '#FF4F9A',
    orange: '#FF9500',
    deeporange: '#FF6B22',
    lime: '#CDEC39',
    white: '#FFFFFF',
    black: '#000000',
  },
  touch: { tapHold: true, touchRipple: true, activeState: true },
  navbar: {
    hideOnPageScroll: true,
    showOnPageScrollEnd: true,
    showOnPageScrollTop: true,
    mdCenterTitle: true,
  },
  toolbar: {
    hideOnPageScroll: true,
    showOnPageScrollEnd: true,
    showOnPageScrollTop: true,
  },
});

const activeLayout = computed(() => AppLayout);

// ── Framework7 boot ───────────────────────────────────────────────────────────
// Replaces the prior setTimeout(150) wall-clock with f7.once('viewInit') keyed
// on the main view (Playbook Section 4 — f7ready resolves once instance is initialized,
// not once views have mounted). On init failure we set initError and keep
// isReady=false so AppLayout never mounts against a half-initialized F7.
f7ready(async (f7) => {
  window.f7Instance = f7;

  await adapter.registerServiceWorker();

  // ── PWA reactive watchers ────────────────────────────────────────────────
  // Placed here (after registerServiceWorker) so watchers are set up only
  // after the SW is registered and f7Instance is assigned. { immediate: true }
  // catches the case where needRefresh/offlineReady flipped during the async
  // register() call before this watcher was created. isDismissed gates the
  // update dialog so a "Later" tap in a session suppresses the re-trigger from
  // workbox re-detecting the same waiting SW (RC1 fix).
  if (!isTma()) {
    // Any SW update (cold-start or mid-session hourly check) shows the prompt
    // once. isDismissed is sessionStorage-backed so it survives the SW-triggered
    // reload, preventing the infinite loop from rapid back-to-back deployments
    // (each deployment creates a new waiting SW; without sessionStorage persistence
    // the dismiss flag would reset on every reload and the dialog would loop).
    // The collector's day applies it by itself instead (onUpdateWaiting).
    watch(
      needRefresh,
      (hasUpdate) => {
        if (hasUpdate) onUpdateWaiting();
      },
      { immediate: true },
    );

    watch(
      offlineReady,
      (ready) => {
        if (ready) showOfflineToast();
      },
      { immediate: true },
    );
  }

  // viewInit fires when <f7-view main> registers its router. Listener must be
  // attached before isReady flips so the event isn't missed in fast boots.
  // Already-initialized case: f7.views.main exists and we short-circuit.
  if (!f7.views.main) {
    f7.once('viewInit', (view) => {
      if (!view.main) return;
      if (!view.router) console.error('[App] Main view initialized without router');
    });
  }

  try {
    // Wait for the environment init (language) to settle before the platform
    // adapter runs. `envInitState` has two shapes: the useAsyncState object,
    // and the plain `{ isReady: ref(true) }` returned when init already ran —
    // `until()` on the getter handles both.
    //
    // The `error` term is not belt-and-braces: useAsyncState sets `isReady`
    // only in its SUCCESS branch, so a throw from initLanguage() would park
    // this await forever and `isReady` below would never flip — a blank app.
    // Language init is best-effort, not a boot gate. The timeout is the last
    // backstop for a shape that settles neither signal; boot continues either
    // way, so `throwOnTimeout` stays false.
    const envSettled = () => envInitState.isReady.value || envInitState.error?.value !== undefined;
    await until(envSettled).toBe(true, { timeout: 5000, throwOnTimeout: false });

    await adapter.initialize();

    const authOptions = {};
    const initDataRaw = adapter.getInitData();
    if (initDataRaw) authOptions.telegramInitData = initDataRaw;

    await initAuth(authOptions);

    // ── Boot-time landing pre-empt (F7 initial-page guard-skip workaround) ────
    // F7-Vue pre-renders the initial URL's eager `component:` into the view DOM
    // before router.init(). When that component renders a real <f7-page>
    // (LoginPage does), F7 sees a `.page` already present and takes the DOM-init
    // path instead of router.navigate() — so the route's `beforeEnter`
    // (redirectIfAuthenticated) NEVER runs on a cold hard-load of /login/
    // (root cause: framework7 router-class.js ~line 880; f7v9-vue3-playbook Section 5).
    // Net effect: an already-authed user who hard-loads /login/ (a stale
    // bookmark, a typed URL) would sit on the login page instead of their shell.
    // Fix: rewrite the URL to the club landing BEFORE the <f7-view> mounts (F7
    // reads document.location at init, gated by isReady), so the view boots
    // straight to the right shell and its real guard runs there. browserHistory-
    // InitialMatch stays true, so supplier cold-boot deep-links are untouched
    // (they never hit this branch — only /login/ and /seller-registration/ do).
    // Two eager-component public routes exhibit the skip and need this pre-empt:
    //   - /login/ (LoginPage renders an <f7-page>)
    //   - /join/  (ClubChoiceScreen, the onboarding entry, renders an <f7-page>)
    // For a MEMBER who hard-loads either, the route's beforeEnter never fires, so
    // we bounce them to their club shell here. /seller-registration/ is now a
    // redirect into /join/ (asyncComponent path) so its guard already fires on
    // hard-load — listed defensively in case it ever becomes eager.
    const bootAuth = useAuthStore();
    // The supplier stage (supplier-showroom P2-A) — read the same way as the
    // guards: `supplierStage`, with `isSupplier` as the cached-session fallback.
    const bootStage = { supplierStage: bootAuth.supplierStage, isSupplier: bootAuth.isSupplier };
    const AUTH_GATED_PUBLIC_PATHS = ['/login/', ONBOARDING_ENTRY, '/seller-registration/'];
    if (bootAuth.isAuthenticated && AUTH_GATED_PUBLIC_PATHS.includes(window.location.pathname)) {
      // Mirror the guards: members → club landing; the membership-less
      // (mid-onboarding) → the onboarding entry (/join/). When already on /join/
      // with no membership, landing === pathname → no rewrite, the user stays.
      const landing = bootAuth.membership?.id ? clubLandingPath(bootStage) : ONBOARDING_ENTRY;
      if (landing !== window.location.pathname) {
        window.history.replaceState({}, '', landing);
      }
    }

    // ── Boot-time auth pre-empt for EAGER protected routes (row-22 guard-skip) ─
    // Mirror of the block above, for the opposite case: an UNAUTHENTICATED user
    // hard-loading a protected route whose guard F7 skips on the initial page.
    // /poola/ uses eager `component:` (PoolaPage renders an <f7-page>) to avoid
    // the asyncComponent "[object Promise]" initial-render leak (playbook Section 5 row
    // 16) — but eager means its requireAuth `beforeEnter` does NOT fire on a cold
    // hard-load (row 22, same DOM-init path as /login/). Without this, a logged-
    // out visitor refreshing/bookmarking /poola/ would see the teaser shell paint
    // before the API-401 backstop bounced them. Rewrite to /login/ BEFORE the
    // <f7-view> mounts so it never paints. Both slash forms (the /poola → /poola/
    // normalizer fires post-mount, too late for this pre-empt). Authed users —
    // member, supplier, OR pending — are allowed through, matching requireAuth.
    // Both the seller-neutral /poola/ and the supplier-namespaced /suppliers/poola/
    // use eager `component:` (PoolaPage renders an <f7-page>) to avoid the
    // asyncComponent "[object Promise]" leak (playbook Section 5 row 16) — so their
    // requireAuth beforeEnter is SKIPPED on a cold hard-load (row 22). Pre-empt
    // both here. The slashless variants are included because the /suppliers/poola →
    // /suppliers/poola/ normalizer fires post-mount (too late for this pre-empt).
    //
    // /referral/ (the member invite screen, id referral) is added here too. It
    // uses eager `component:` (ReferralPage renders an <f7-page>) for the same
    // reason /poola/ does, so its requireMembership `beforeEnter` is likewise
    // SKIPPED on a cold hard-load (row 22) and an unauthenticated visitor would
    // otherwise paint it. requireMembership has TWO tiers, but only the
    // unauthenticated one belongs in this block: an authed member WITHOUT a
    // membership is bounced to onboarding by the guard on every soft nav, and on
    // a cold load lands on a screen whose composable returns not_activated →
    // the actionable "activate your membership" copy (never a blank paint). The
    // Profile entry into /referral/ is itself gated to activated sellers
    // client-side (locked-state gating), so that tier arriving cold is already an
    // edge. Pre-empting only the unauthenticated tier here matches /poola/.
    const EAGER_AUTH_REQUIRED_PATHS = [
      '/poola/',
      '/poola',
      '/suppliers/poola/',
      '/suppliers/poola',
      // «البولة اللي فاتت» — the last-Poola page, in both namespaces. Eager
      // component, requireAuth skipped on a cold hard-load (row 22), same as
      // the Poola page it is pushed from.
      '/poola/last/',
      '/poola/last',
      '/suppliers/poola/last/',
      '/suppliers/poola/last',
      '/referral/',
      '/referral',
      // «اقتراحاتي» — eager component, requireMembership guard skipped on a
      // cold hard-load (row 22), same as /referral/.
      '/suggestions/',
      '/suggestions',
    ];
    if (!bootAuth.isAuthenticated && EAGER_AUTH_REQUIRED_PATHS.includes(window.location.pathname)) {
      window.history.replaceState({}, '', '/login/');
    }

    // Prefix variant of the block above, for PARAM-carrying eager protected
    // routes whose exact path can't be listed. /suppliers/box/:boxId/ (the
    // supplier order detail, id supplier-box-detail) uses eager `component:`
    // because a push / PWA cold-launch deep-link can make it the boot URL —
    // asyncComponent would leak "[object Promise]" on first paint (playbook Section 5
    // row 16). Eager means its requireSupplier `beforeEnter` is SKIPPED on a cold
    // hard-load (row 22, same DOM-init path as /login/ and /poola/): F7 pre-renders
    // the eager component, finds an <f7-page>, and takes the DOM-page path instead
    // of router.navigate(), so the guard never runs.
    //
    // Unlike /poola/ (guarded by requireAuth — a single auth tier), this route's
    // requireSupplier has FOUR tiers, so the pre-empt must mirror ALL of them or
    // an authed-but-ineligible user still sails past the skipped guard and paints
    // the detail shell + fires GET /supplier-boxes/{id} (403/error loop) instead of
    // the clean bounce a soft-nav's guard would give:
    //   - unauthenticated        → /login/
    //   - no active membership   → onboarding (/join/)
    //   - an `interested` lead   → his waiting screen
    //   - a seller (stage none)  → their club landing
    // `showroom` and `active` stay. A prefix (not exact) match is required for the
    // :boxId param; the strict /suppliers/box/:boxId/ route + its slashless
    // normalizer both live under this prefix, so both slash forms are covered.
    // Rewrite BEFORE the <f7-view> mounts. The decision is
    // router/bootPreempts.js `supplierRoutePreempt` (tested there).
    const supplierPreempt = supplierRoutePreempt(window.location.pathname, bootAuth);
    if (supplierPreempt) {
      window.history.replaceState({}, '', supplierPreempt);
    }

    // Prefix pre-empt for the member-answerable issue routes (/issues/ hub +
    // /issues/:issueId/ card). Both are eager `component:` (initial-URL eligible —
    // the ItemIssueNeedsDecision notification deep-links to /issues/{id}/), so
    // their requireMembership `beforeEnter` is SKIPPED on a cold hard-load (row
    // 22). Like /referral/, only the UNAUTHENTICATED tier is pre-empted here: an
    // authed member lands on the card/inbox; an authed-no-membership user is
    // bounced by the soft-nav guard and, cold, lands on the composable's graceful
    // state (the fetch 403/404s → the calm "already handled" screen), never a
    // blank paint. A prefix match covers the :issueId param + both slash forms.
    //
    // '/posts/' joins it for the same reason: /posts/:id/ is the deep-link target
    // of the `product.unavailable` fan-out row, eager `component:` for the same
    // cold-boot reason, and therefore skips its own requireMembership on a cold
    // hard-load. Its unauthenticated tier bounces to /login/ here; an authed
    // non-member landing cold gets the page's own not-found state from the 403,
    // never a blank paint.
    //
    // '/orders/box/' joins it for the same reason (tips-wave P3-A, owner answer
    // W-K): the carton route is eager `component:` because the out-of-stock /
    // suggestion-outcome / ready-missing-items pushes deep-link straight to it,
    // so it is initial-URL eligible and skips its own requireMembership cold.
    //
    // '/tadbeesa/' (the club's تدبيسة shelf, tadbeesa P3-A) joins it too: eager
    // `component:` because a reload makes it the boot URL, so its
    // requireMembership is skipped cold. The slashless '/tadbeesa' needs no
    // entry: it matches only the redirect route, which has no component to
    // pre-render, so F7 boots it through router.navigate() and the strict
    // route's guard runs (tests/e2e/tadbeesa-page.spec.js covers both forms).
    // The list lives in router/bootPreempts.js beside the supplier tier below.
    if (
      !bootAuth.isAuthenticated &&
      EAGER_MEMBER_REQUIRED_PREFIXES.some((p) => window.location.pathname.startsWith(p))
    ) {
      window.history.replaceState({}, '', '/login/');
    }

    // The supplier tier of the seller-route pre-empts (supplier-showroom P2-A).
    // An authenticated user supplier-side (any stage but `none`) cold-loading a
    // seller member route — the prefixes above, /referral/, /suggestions/ — is
    // rewritten to his landing, as requireMembership does on a soft nav (the
    // "cold-loaded post page skipped the supplier guard" fix); on /poola/ or
    // /poola/last/ he goes to the supplier twin, as the seller Poola routes'
    // guard does (no seller tabbar, no seller deposit banner). A seller is
    // untouched. Decision: router/bootPreempts.js `sellerRoutePreempt`.
    const sellerPreempt = sellerRoutePreempt(window.location.pathname, bootAuth);
    if (sellerPreempt) {
      window.history.replaceState({}, '', sellerPreempt);
    }

    // The collector's per-shop supplier preview wins the boot (supplier-preview
    // P2-A, L-H): while it is on, any path outside /suppliers/ and /join/gains/
    // becomes /suppliers/ — before the pure-collector rewrite below, so his day
    // (/collector/*) never paints while the shop owner may hold the phone; the
    // banner's «اقفل العرض» is the only way back. This is also the /collector/
    // cold-load pre-empt's preview tier: the block below then sees /suppliers/.
    // Decision: router/bootPreempts.js `supplierPreviewPreempt`.
    const previewPreempt = supplierPreviewPreempt(window.location.pathname, bootAuth);
    if (previewPreempt) {
      window.history.replaceState({}, '', previewPreempt);
    }

    // Prefix pre-empt for the collector's day (collector-app P1-C). Both
    // /collector/ and /collector/shops/:supplierId/ are eager `component:` (a
    // reload in the market makes either the boot URL), so requireCollector is
    // SKIPPED on a cold hard-load (row 22). Mirror its three tiers here, before
    // the <f7-view> mounts: unauthenticated → /login/; authed non-collector →
    // their club landing, or onboarding when they have no membership. A
    // collector — with or without a club membership — stays.
    // Two phase-2 additions (collector-app P2-B): a collector HELD on his day
    // after an expired login (L-D) is signed out but stays; and a collector
    // with no club who opens the installed icon (start_url '/') or lands on
    // /home/ goes to his day — '/' would bounce him to onboarding, with the
    // day he kept offline out of reach. Not inside Telegram (L-A).
    const bootPath = window.location.pathname;
    const heldCollector = !isTma() && !bootAuth.isAuthenticated && collectorHoldActive();
    const collectorLanding = collectorBootPreempt(bootPath, bootAuth, { inTma: isTma(), held: heldCollector });
    if (collectorLanding) {
      window.history.replaceState({}, '', collectorLanding);
    }
    // A hold with nothing left on the phone (the plan closed and was cleared)
    // is dropped: the signed-out visitor goes to /login/ like anyone else.
    if (heldCollector) {
      const hold = readHold();
      hasLocalWork(hold?.userId)
        .then((has) => {
          if (has) return null;
          clearHold();
          if (isCollectorPath()) {
            f7ready((f7) => f7.views.main?.router?.navigate('/login/', { clearPreviousHistory: true }));
          }
          return null;
        })
        .catch(() => {
          /* kept */
        });
    }

    const EAGER_COLLECTOR_PREFIX = '/collector/';
    if (window.location.pathname.startsWith(EAGER_COLLECTOR_PREFIX)) {
      let collectorPreempt = null;
      if (!bootAuth.isAuthenticated) {
        collectorPreempt = heldCollector ? null : '/login/';
      } else if (!bootAuth.isCollector && bootAuth.user?.isCollector !== undefined) {
        // (a session cached before the field existed has no value yet — let
        // it through, as requireCollector does; the backend 403s the rest)
        collectorPreempt = bootAuth.hasActiveMembership ? clubLandingPath(bootStage) : ONBOARDING_ENTRY;
      }
      if (collectorPreempt) {
        window.history.replaceState({}, '', collectorPreempt);
      }
    }

    isReady.value = true;

    // ── The collector's phone queue (collector-app P2-B) ─────────────────────
    // Armed for any collector once auth resolves — not only on /collector/ — so
    // a leader who lands on /home/ still sends what the phone kept. Never
    // inside Telegram: the day does not live there (L-A). Lazy: the member
    // app does not load the collector layer.
    if (!isTma()) {
      watch(
        () => useAuthStore().isCollector || collectorHoldActive(),
        (isCollector) => {
          if (!isCollector) return;
          import('@/collector/actions.js')
            .then(({ useCollectorActionsStore }) => useCollectorActionsStore().start())
            .catch((err) => console.warn('[App] collector queue start failed:', err));
        },
        { immediate: true },
      );
    }

    // ── Boot-time unread-count reconcile (gap G18) ───────────────────────────
    // The tab-bar badge reads a PERSISTED unreadCount that can be stale on cold
    // boot (and after the Telegram rail delivered an event while the app was
    // closed). Reconcile once from the dedicated endpoint so the badge never
    // lies before the user opens the notification center. Non-blocking +
    // best-effort; resolving the event on EITHER rail keeps the count honest.
    try {
      const bootAuthStore = useAuthStore();
      if (bootAuthStore.isAuthenticated && bootAuthStore.user?.id) {
        fetchUnreadCount(bootAuthStore.user.id)
          .then((count) => useNotificationStore().setUnreadCount(count))
          .catch(() => {
            /* badge stays from persisted state */
          });
      }
    } catch {
      /* noop */
    }

    // ── Realtime bootstrap (Reverb/Echo foundation) ──────────────────────────
    // Open the websocket ONCE for authenticated users, after auth resolves.
    // Foundation only: this opens the connection + wires connection-status and
    // reconnect/visibility hooks; it subscribes to NO channel (feature screens
    // do that later via echo-vue composables). Gated on isAuthenticated because
    // every Poolit channel is a private Sanctum channel — a logged-out socket
    // has nothing to subscribe to and would only fail broadcasting-auth. Errors
    // here must never block the boot, so this is best-effort.
    try {
      // Not under an offline supplier preview (supplier-preview-offline L-J).
      if (bootAuth.isAuthenticated && !isReplayOn()) {
        initRealtime();
      }
    } catch (err) {
      console.error('[App] realtime init failed:', err);
    }

    // The collector's offline supplier preview (supplier-preview-offline L-J):
    // a picture not on the phone is hidden, not broken. (A DELETE a live preview
    // still owes rides the tap queue's triggers — collector/previewLeads.js.)
    if (!isTma() && isReplayOn()) hideMissingImages();

    // ── Cold-start pending-push-route drain ──────────────────────────────────
    // A push tap can capture a route BEFORE the f7 router has mounted:
    //   1. SW notificationclick handler posts PUSH_NAVIGATE before App.vue's
    //      message listener was bound (true cold-start)
    //   2. OneSignal page-side click fires inside usePushNotifications'
    //      navigateTo() while window.f7Instance is still null
    // Both paths persist the route to BOTH window.__POOLIT_PENDING_PUSH_ROUTE
    // (in-memory, lost on reload) AND sessionStorage.poolit:pending-push-route
    // (survives cold-start). Drain both on the next tick so the f7 view mount
    // has settled before navigate runs.
    nextTick()
      .then(() => {
        let pending = null;
        try {
          pending = window[PENDING_PUSH_ROUTE_GLOBAL] || null;
        } catch {
          /* noop */
        }
        if (!pending) {
          try {
            pending = sessionStorage.getItem(PENDING_PUSH_ROUTE_SS_KEY);
          } catch {
            /* noop */
          }
        }
        if (!pending || !isSafeOriginRoute(pending)) return null;
        try {
          window[PENDING_PUSH_ROUTE_GLOBAL] = null;
        } catch {
          /* noop */
        }
        try {
          sessionStorage.removeItem(PENDING_PUSH_ROUTE_SS_KEY);
        } catch {
          /* noop */
        }
        try {
          pushNavigateTo(pending);
        } catch (err) {
          console.warn('[App] pending push route drain failed:', err);
        }
        return null;
      })
      .catch(() => {
        /* noop */
      });

    // ── Push notifications boot gate (PWA-only) ──────────────────────────────
    // Must run AFTER auth resolves (so ensurePushIdentity inside initialize()
    // sees the freshly-landed user.id). PwaAdapter.initPushNotifications() is
    // the boolean TMA gate; the actual composable invocation lives here so
    // onScopeDispose binds.
    //
    // The boot-time `initPush()` only handles the returning-grantee silent
    // path. For non-grantees the priming dialog is NO LONGER attached to a
    // document-gesture listener (gap G6) — instead it fires from the
    // `push:trigger-priming` event bus when the user reaches a real D4
    // value-moment (order-box item save, deposit submit, registration).
    if (!isTma() && adapter.initPushNotifications && adapter.initPushNotifications()) {
      nextTick()
        .then(async () => {
          await initPush();
          return null;
        })
        .catch((err) => {
          console.warn('[App] Push init failed:', err);
        });

      // Subscribe to value-moment triggers. The bus is also exported as
      // PUSH_PRIMING_TRIGGER_EVENT from usePushNotifications.js for emitters.
      primingTriggerBus.on((payload) => {
        const moment = payload && typeof payload === 'object' ? payload.moment : null;
        if (typeof moment !== 'string' || !moment) {
          console.warn('[App] push:trigger-priming received without a moment string');
          return;
        }
        // The scheduler gates it (club moments, one overlay at a time) and
        // calls presentPushNudge when it reaches the head of the queue.
        enqueueNudge({ kind: 'push', moment, reason: 'bus' });
      });
    }

    // ⚠️ DEV-ONLY: simulate a Telegram `start_param` from the URL query so the
    // referral-capture path can be exercised on the PWA dev origin
    // (app.poolit.test) without a real Telegram launch. Append
    // `?startapp=ref_<code>` to the URL. Dead-code-eliminated from production
    // (import.meta.env.DEV gate); devReferral.js is not bundled in prod.
    if (import.meta.env.DEV) {
      try {
        const { injectReferralFromUrl } = await import('@/utils/devReferral.js');
        injectReferralFromUrl();
      } catch (err) {
        // eslint-disable-next-line no-console -- dev-only, non-fatal
        console.warn('[App] dev referral injection failed:', err);
      }
    }

    // Telegram `start_param` routing. Three things share this one slot, and
    // they are disjoint by construction: the exact-match `launch` handoff verb
    // (spec-telegram-launch-flow.md), `ref_…`/`ref-…` supplier referral tokens
    // (spec-supplier-referral-poolshare-gift.md), and `go_…` notification deep
    // links. Non-fatal — any failure here just leaves the default route active.
    if (isTma()) {
      try {
        const { retrieveLaunchParams } = await import('@telegram-apps/sdk-vue');
        const launchParams = retrieveLaunchParams();
        const startParam = launchParams?.tgWebAppStartParam;
        // Supplier referral capture (spec-supplier-referral-poolshare-gift.md).
        // Independent of the `launch` verb below — a no-op for `launch`/
        // empty/unknown values, so it never disturbs that branch.
        captureReferralFromStartParam(startParam);

        // The main view may not exist yet at this point in boot, so every
        // start_param destination defers to `viewInit` rather than dropping the
        // navigation on the floor.
        const navigateWhenViewReady = (path, options = {}) => {
          const go = () => f7.views.main?.router?.navigate(path, options);
          if (f7.views.main) {
            go();
          } else {
            f7.once('viewInit', (view) => {
              if (view.main) go();
            });
          }
        };

        if (startParam === 'launch') {
          navigateWhenViewReady('/tg-launch/', { clearPreviousHistory: true });
        } else {
          // Notification deep link — the "فتح" button on a Telegram notification
          // carries the destination route base64url-encoded behind a `go_`
          // prefix. See the backend brief
          // _bmad-output/sync/briefs/2026-08-14-telegram-cta-miniapp-deeplink.md.
          //
          // History is deliberately NOT cleared (unlike `launch`, which is a
          // terminal handoff page): the member should be able to back out of the
          // notification's screen into the normal tab shell.
          const deepLinkRoute = extractDeepLinkRoute(startParam);
          if (deepLinkRoute) {
            navigateWhenViewReady(deepLinkRoute);
          }
        }
      } catch (err) {
        console.warn('[App] start_param routing failed:', err);
      }
    }
  } catch (error) {
    console.error('[App] Initialization error:', error);
    initError.value = error;
  }
});

// Module-scope refs let helpers tear down imperative F7 instances
// (dialog + preloader + offline toast) on dismiss / completion. Without this
// the DOM nodes and listeners persist after close. F7 playbook Section 5 row 4.
let updateDialogInstance = null;
let updatePreloaderInstance = null;
let offlineToastInstance = null;

function destroyUpdateDialog() {
  if (!updateDialogInstance) return;
  updateDialogInstance.close();
  updateDialogInstance.destroy();
  updateDialogInstance = null;
}

function destroyUpdatePreloader() {
  if (!updatePreloaderInstance) return;
  updatePreloaderInstance.close();
  updatePreloaderInstance.destroy();
  updatePreloaderInstance = null;
}

function promptUpdateUnlessDismissed() {
  if (!isDismissed.value) showUpdateDialog();
}

// A worker is waiting. Decided once auth has resolved (the policy reads who is
// signed in): members get the prompt; the collector's day applies it when
// idle — never a dialog over a shop visit, never «لاحقاً» leaving the photo
// cache to an old worker (decision D3; collector/prewarm.js). The auto path
// ignores the «لاحقاً» flag; its 10-minute fallback prompt respects it.
function onUpdateWaiting() {
  if (!isReady.value) {
    const stopReadyWatch = watch(isReady, (ready) => {
      if (!ready) return;
      stopReadyWatch();
      onUpdateWaiting();
    });
    return;
  }
  if (updatePolicyFor(useAuthStore(), { onCollectorDay: isCollectorPath() }) !== 'auto-at-idle') {
    promptUpdateUnlessDismissed();
    return;
  }
  // The store is created only here (its setup reads the phone's preview
  // copies): a member never gets this far.
  const prewarm = useCollectorPrewarmStore();
  applyUpdateWhenIdle({
    blockers: [
      () => prewarm.phase === 'running' && 'prewarm',
      () => authPaused.value && 'auth-paused',
      // Decided when the worker started waiting; a leader who is also a member
      // and has left his day (or anyone signed out off it) is a member again:
      // never reloaded by itself there. Ten minutes of that and the member's
      // prompt comes, through the fallback.
      () => updatePolicyFor(useAuthStore(), { onCollectorDay: isCollectorPath() }) !== 'auto-at-idle' && 'member-shell',
    ],
    onFallback: promptUpdateUnlessDismissed,
  });
}

function showUpdateDialog() {
  const f7 = window.f7Instance;
  if (!f7) {
    f7ready(() => showUpdateDialog());
    return;
  }

  // Defer until app boot completes (auth resolved, AppLayout mounted).
  // Without this guard the dialog stacks on top of LoadingLayout when a
  // new SW is detected during a slow boot. Single-shot watcher: stop
  // itself on first true so a later re-flip won't fire it again.
  // PWA playbook Section 3 "Three F7-specific gotchas" point 1.
  if (!isReady.value) {
    const stopReadyWatch = watch(isReady, (ready) => {
      if (!ready) return;
      stopReadyWatch();
      showUpdateDialog();
    });
    return;
  }

  // Defer if any caller has acquired a reload-hold: useAiStream while an
  // EventSource is in flight (clicking "Reload" mid-stream would lose the
  // streamed response), an open nudge, and the collector's three queues while
  // they flush. Single-shot watcher that re-fires once the count drops back to
  // 0. PWA audit MEDIUM-4.
  if (isReloadBusy.value) {
    const stopBusyWatch = watch(isReloadBusy, (busy) => {
      if (busy) return;
      stopBusyWatch();
      showUpdateDialog();
    });
    return;
  }

  // Defer if a router transition is in flight. F7 v9 sets
  // router.allowPageChange = false at transition start and flips it back
  // to true once `pageAfterIn` fires for the new page (verified at
  // node_modules/framework7/modules/router/navigate.js:71 and back.js:33).
  // Opening the dialog mid-transition stacks the overlay on two layered
  // pages, producing a visible artifact on slow devices.
  const router = f7.views.main?.router;
  if (router && router.allowPageChange === false) {
    f7.once('pageAfterIn', () => showUpdateDialog());
    return;
  }

  // Defensive: dismiss any prior instance before opening a new one.
  destroyUpdateDialog();

  // F7's buttonOnClick handler reads closure state both before AND after our
  // onClick. Calling destroy() inside onClick clears that closure state and
  // F7's post-handler access throws `Cannot read properties of undefined`.
  // Defer destroy via microtask so F7's handler completes first.
  const deferredDestroyUpdate = () => {
    Promise.resolve()
      .then(() => {
        try {
          destroyUpdateDialog();
        } catch {
          /* noop */
        }
        return null;
      })
      .catch(() => {
        /* noop — destroy is best-effort */
      });
  };

  updateDialogInstance = f7.dialog.create({
    title: 'تحديث متاح',
    text: 'يتوفر إصدار جديد. هل تريد التحديث الآن؟',
    buttons: [
      {
        text: 'لاحقاً',
        onClick: () => {
          dismissUpdate();
          deferredDestroyUpdate();
        },
      },
      {
        text: 'تحديث',
        bold: true,
        onClick: () => {
          deferredDestroyUpdate();
          // Create preloader AFTER current dialog is torn down; chain via
          // microtask so the SW update kick-off doesn't race the destroy.
          Promise.resolve()
            .then(() => {
              try {
                destroyUpdatePreloader();
              } catch {
                /* noop */
              }
              updatePreloaderInstance = f7.dialog.create({
                title: '',
                text: 'جاري التحديث...',
                preloader: true,
              });
              updatePreloaderInstance.open();
              // Clear the dismiss flag so the NEXT genuine update after this
              // reload can still prompt the user. We don't want to suppress
              // future updates just because they dismissed a previous one.
              try {
                sessionStorage.removeItem('pwa:dismissed');
              } catch {
                /* noop */
              }
              // updateServiceWorker(true) posts SKIP_WAITING; workbox-window's
              // `controlling` listener inside virtual:pwa-register/vue fires
              // window.location.reload() once the new SW activates. No second
              // reload here — adding one races the internal handler and on slow
              // networks lands before `controlling`, reloading without the new
              // SW in control.
              updateServiceWorker(true)
                .finally(destroyUpdatePreloader)
                .catch((err) => console.error('[App] SW update failed:', err));
              return null;
            })
            .catch(() => {
              /* noop — preloader chain best-effort */
            });
        },
      },
    ],
  });
  updateDialogInstance.open();
}

function destroyOfflineToast() {
  if (!offlineToastInstance) return;
  offlineToastInstance.close();
  offlineToastInstance.destroy();
  offlineToastInstance = null;
}

function showOfflineToast() {
  const f7 = window.f7Instance;
  if (!f7) {
    f7ready(() => showOfflineToast());
    return;
  }

  // Defer until app boot completes (auth resolved, AppLayout mounted).
  // Without this guard the toast renders over LoadingLayout when offlineReady
  // flips during a slow boot. Mirrors the same guard in showUpdateDialog().
  // PWA playbook Section 3 "Three F7-specific gotchas" point 1; F7 playbook Section 5 row 5.
  if (!isReady.value) {
    const stopReadyWatch = watch(isReady, (ready) => {
      if (!ready) return;
      stopReadyWatch();
      showOfflineToast();
    });
    return;
  }

  // Defensive: dispose any prior instance before creating a new one.
  destroyOfflineToast();

  offlineToastInstance = f7.toast.create({
    text: '✅ التطبيق جاهز للعمل بدون إنترنت',
    position: 'bottom',
    closeTimeout: 3_000,
    on: {
      close: () => {
        dismissOfflineReady();
        destroyOfflineToast();
      },
    },
  });
  offlineToastInstance.open();
}

// ── Push subscription success toast (gap GR-1) ────────────────────────────────
// The priming dialog tears itself down the instant optIn() resolves, leaving
// the user with NO confirmation the subscribe worked (frontend-map:136-139).
// This bottom toast is the on-camera "you're subscribed" beat — per GR-5 the
// journey's welcome IS this toast, not a lock-screen push. Fired ONLY on a true
// optIn() success returned by firePushOptIn().then(ok). Module-scope instance +
// destroy so the DOM node/listeners don't leak (F7 playbook Section 5 row 4).
let pushSubscribedToastInstance = null;

function destroyPushSubscribedToast() {
  if (!pushSubscribedToastInstance) return;
  pushSubscribedToastInstance.close();
  pushSubscribedToastInstance.destroy();
  pushSubscribedToastInstance = null;
}

function showPushSubscribedToast() {
  const f7 = window.f7Instance;
  if (!f7?.toast?.create) return;
  // Defensive: dispose any prior instance before creating a new one.
  destroyPushSubscribedToast();
  pushSubscribedToastInstance = f7.toast.create({
    text: t('push.priming.subscribed'),
    position: 'bottom',
    closeTimeout: 3_500,
    on: {
      closed: () => {
        destroyPushSubscribedToast();
      },
    },
  });
  pushSubscribedToastInstance.open();
}

// ── Push priming dialog (PWA-only, event-triggered, moment-aware) ─────────────
// Per gap G6/G7/G8/G10 the priming dialog is now driven by an event bus fired
// from real value-moments (order-box item save success, deposit submit, the
// registration screen, the profile status card). The previous document-gesture trigger (Phase-1b) burned
// the one-shot iOS prompt on a context-free tap — see
// _bmad-output/planning-artifacts/2026-06-05-push-notifications-ux-dashboard-research.md Section 1.
//
// Flow:
//   1. Emitter (e.g. DepositSheet success) → primingTriggerBus.emit({ moment }).
//   2. The bus handler queues a `push` nudge (useEngagementNudges.js); when it
//      reaches the head of the queue the scheduler calls presentPushNudge(),
//      which warms init (warmPushInit — covers the heavy await chain BEFORE the
//      click) and branches on pushPreflightDecision:
//        - 'soft-ask'        → openPrimingDialog(moment).
//        - 'settings-helper' → openSettingsHelperDialog().
//        - 'telegram-helper' → openTelegramHelperDialog().
//        - 'skip'            → nothing.
//   3. The Enable click handler does only firePushOptIn() so the iOS gesture-
//      activation token is preserved through the native prompt (research Section 1.5).
//
// The scheduler holds holdReload('nudge') from the moment a push nudge is
// shown until its dialog closes, so an SW update detected mid-prompt waits for
// it (PWA playbook Section 3 "Update during AI stream" — same mechanism). Each
// dialog calls its `onClosed` once, after its deferred destroy, and that is
// what completes the nudge.
let primingDialogInstance = null;
let primingShownFor = null; // Stores the moment string of the open dialog or null.
// The longest the soft-ask waits for OneSignal to warm before it opens anyway.
const PUSH_WARM_TIMEOUT_MS = 4_000;
// The longest an Enable tap holds the nudge open waiting for the native prompt's
// answer; the dialog itself has already closed. A late answer is still saved.
const PUSH_OPT_IN_TIMEOUT_MS = 15_000;

// The push dialogs are created with `destroyOnClose`: F7 destroys each after
// its close animation, once its own `closed` cleanup has removed the element.
// Destroying it ourselves inside the button's microtask skipped that cleanup
// and left an invisible `.dialog.modal-out` (opacity 0, pointer-events auto) in
// the middle of the screen, swallowing taps. So here we only close.
function destroyPrimingDialog() {
  if (!primingDialogInstance) return;
  if (!primingDialogInstance.destroyed) primingDialogInstance.close();
  primingDialogInstance = null;
}

// Translate a moment slug into i18n key paths. Fallback to generic keys when
// a moment-specific variant is missing so authoring a new moment never crashes.
function primingKeysForMoment(moment) {
  const namespace = `push.priming.${moment}`;
  const base = 'push.priming';
  const hasMomentTitle = te(`${namespace}.title`);
  return {
    title: hasMomentTitle ? `${namespace}.title` : `${base}.title`,
    body: hasMomentTitle ? `${namespace}.body` : `${base}.body`,
    enable: hasMomentTitle ? `${namespace}.enable` : `${base}.enable`,
    notNow: hasMomentTitle ? `${namespace}.notNow` : `${base}.notNow`,
  };
}

// The scheduler's push presenter: resolves once the nudge is over — at once
// when there is nothing to show, else when the dialog it opened has closed.
function presentPushNudge({ moment }) {
  return new Promise((resolve) => {
    runPushNudge(moment, resolve).catch((err) => {
      console.warn('[App] push nudge failed:', err);
      resolve();
    });
  });
}

async function runPushNudge(moment, onClosed) {
  if (primingShownFor) {
    onClosed();
    return;
  }

  const authStore = useAuthStore();
  const userId = authStore.user?.id ?? null;

  let decision;
  try {
    decision = pushPreflightDecision(moment, userId);
  } catch (err) {
    console.warn('[App] pushPreflightDecision threw:', err);
    onClosed();
    return;
  }
  if (!decision || decision.action === 'skip') {
    onClosed();
    return;
  }

  if (decision.action === 'settings-helper') {
    openSettingsHelperDialog(onClosed);
    return;
  }
  if (decision.action === 'telegram-helper') {
    openTelegramHelperDialog(onClosed);
    return;
  }

  // soft-ask path — warm OneSignal BEFORE the dialog opens so the Enable
  // click handler can call firePushOptIn() with near-zero awaited work
  // ahead of the native prompt. iOS WebKit's transient-activation window
  // is ~1s; pre-warming protects it (research Section 1.5).
  //
  // Bounded: the warm-up awaits `navigator.serviceWorker.ready` and the
  // OneSignal SDK, either of which can stay pending for good (no worker, a
  // blocked CDN). This nudge holds the scheduler's queue and a reload hold, so
  // an unbounded wait would stall every later nudge and the update dialog.
  let warmTimer = null;
  try {
    await Promise.race([
      warmPushInit(),
      new Promise((resolve) => {
        warmTimer = setTimeout(resolve, PUSH_WARM_TIMEOUT_MS);
      }),
    ]);
  } catch (err) {
    console.warn('[App] warmPushInit failed (continuing to dialog):', err);
  } finally {
    clearTimeout(warmTimer);
  }
  // The scheduler checked for other overlays before the warm-up; one may have
  // opened since (a sheet, a navigation). Then this nudge ends unshown and
  // goes back in the queue, which waits for the overlay to close.
  // Signed out meanwhile (logout, a 401): the ask is over.
  if (!useAuthStore().isAuthenticated) {
    onClosed();
    return;
  }
  const router = window.f7Instance?.views?.main?.router;
  if (document.querySelector('.modal-in:not(.toast)') || router?.allowPageChange === false) {
    onClosed('requeued');
    setTimeout(() => enqueueNudge({ kind: 'push', moment, reason: 'requeued' }), 0);
    return;
  }
  openPrimingDialog(moment, onClosed);
}

function openPrimingDialog(moment, onClosed) {
  if (primingShownFor || !window.f7Instance) {
    onClosed();
    return;
  }
  const f7 = window.f7Instance;
  primingShownFor = moment;

  const authStore = useAuthStore();
  const userId = authStore.user?.id ?? null;
  const telegramLinked = authStore.telegramLinked === true;
  const keys = primingKeysForMoment(moment);

  destroyPrimingDialog();

  // Once per dialog: a late answer (after the opt-in bound below) must not
  // tear down a newer dialog or complete a later nudge.
  let settled = false;
  const deferredDestroy = () => {
    if (settled) return;
    settled = true;
    Promise.resolve()
      .then(() => {
        try {
          destroyPrimingDialog();
        } catch {
          /* noop */
        }
        primingShownFor = null;
        onClosed();
        return null;
      })
      .catch(() => {
        /* noop — destroy is best-effort */
      });
  };

  // Reassurance line — only when the user has NOT linked Telegram yet (so
  // the «أو سنراسلك على تيليجرام في كل الأحوال» line stays honest).
  const body = telegramLinked
    ? t(keys.body)
    : `${t(keys.body)} ${t('push.priming.telegramReassurance')}`;

  try {
    setPrimingState(userId, { status: 'asked', lastMoment: moment });
  } catch {
    /* noop */
  }

  primingDialogInstance = f7.dialog.create({
    destroyOnClose: true,
    title: t(keys.title),
    text: body,
    buttons: [
      {
        text: t(keys.notNow),
        onClick: () => {
          try {
            setPrimingState(userId, { status: 'soft-declined', lastMoment: moment });
          } catch {
            /* noop */
          }
          deferredDestroy();
        },
      },
      {
        text: t(keys.enable),
        bold: true,
        onClick: () => {
          // CRITICAL — gap G7: do NOT await anything before firePushOptIn().
          // The synchronous SDK call inside firePushOptIn() (constructed not
          // as async) keeps the iOS gesture-activation token alive through
          // the native permission prompt. Identity bind + verification run
          // after optIn() resolves and never block the gesture.
          if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            deferredDestroy();
            return;
          }
          setTimeout(deferredDestroy, PUSH_OPT_IN_TIMEOUT_MS);
          firePushOptIn()
            .then((ok) => {
              try {
                setPrimingState(userId, {
                  status: ok ? 'granted' : 'denied',
                  lastMoment: moment,
                });
              } catch {
                /* noop */
              }
              // GR-1: the on-camera "you're subscribed" confirmation. Only on a
              // true optIn() success — a denied/blocked prompt resolves ok=false
              // and must NOT show a success toast.
              if (ok) showPushSubscribedToast();
              deferredDestroy();
              return null;
            })
            .catch((err) => {
              console.error('[App] firePushOptIn failed:', err);
              try {
                setPrimingState(userId, { status: 'denied', lastMoment: moment });
              } catch {
                /* noop */
              }
              deferredDestroy();
            });
        },
      },
    ],
  });
  primingDialogInstance.open();
}

// Denied → "enable in Settings" helper. Routes to the in-app preferences
// page where the user can also confirm Telegram is the fallback channel.
// (Research Section 1.3: never render a doomed soft-ask when the OS prompt is
// already dead — surface a decision-reversal path instead.)
function openSettingsHelperDialog(onClosed) {
  if (primingShownFor || !window.f7Instance) {
    onClosed();
    return;
  }
  const f7 = window.f7Instance;
  primingShownFor = 'settings-helper';

  const authStore = useAuthStore();
  const userId = authStore.user?.id ?? null;

  destroyPrimingDialog();

  // Once per dialog: a late answer (after the opt-in bound below) must not
  // tear down a newer dialog or complete a later nudge.
  let settled = false;
  const deferredDestroy = () => {
    if (settled) return;
    settled = true;
    Promise.resolve()
      .then(() => {
        try {
          destroyPrimingDialog();
        } catch {
          /* noop */
        }
        primingShownFor = null;
        onClosed();
        return null;
      })
      .catch(() => {
        /* noop */
      });
  };

  // Persist denial so future triggers route here directly instead of
  // re-checking Notification.permission (which can be briefly stale).
  try {
    setPrimingState(userId, { status: 'denied', lastMoment: 'settings-helper' });
  } catch {
    /* noop */
  }

  primingDialogInstance = f7.dialog.create({
    destroyOnClose: true,
    title: t('push.priming.settingsHelper.title'),
    text: t('push.priming.settingsHelper.body'),
    buttons: [
      {
        text: t('push.priming.settingsHelper.notNow'),
        onClick: () => {
          deferredDestroy();
        },
      },
      {
        text: t('push.priming.settingsHelper.openSettings'),
        bold: true,
        onClick: () => {
          try {
            const target = notificationSettingsPath();
            if (target) f7.views.main?.router?.navigate(target);
          } catch (err) {
            console.warn('[App] navigate to preferences failed:', err);
          }
          deferredDestroy();
        },
      },
    ],
  });
  primingDialogInstance.open();
}

// The settings page of the user's club notification center — derived from the
// club's `notificationsRoot` (clubs/manifest.js), never a hard-coded seller path:
// requireMembership on the old target sent a supplier to the Wall (audit
// 2026-11-03, Section 3.7). Null for a member-less collector (no center).
function notificationSettingsPath() {
  const authStore = useAuthStore();
  const { notificationsRoot } = engagementFor({
    supplierStage: authStore.supplierStage,
    isSupplier: authStore.isSupplier,
    isCollector: authStore.isCollector,
    hasActiveMembership: authStore.hasActiveMembership,
  });
  return notificationsRoot ? `${notificationsRoot}preferences/` : null;
}

// Unsupported environment (no-GMS Huawei, Android-without-FCM) →
// surface the Telegram-link helper at the same money-moment so the user
// can still receive the alerts via the reliable rail. (Research Section 1.3 + Section 4.1
// — Telegram is a first-class fallback for MENA, not a degraded backup.)
function openTelegramHelperDialog(onClosed) {
  if (primingShownFor || !window.f7Instance) {
    onClosed();
    return;
  }
  const f7 = window.f7Instance;
  primingShownFor = 'telegram-helper';

  destroyPrimingDialog();

  // Once per dialog: a late answer (after the opt-in bound below) must not
  // tear down a newer dialog or complete a later nudge.
  let settled = false;
  const deferredDestroy = () => {
    if (settled) return;
    settled = true;
    Promise.resolve()
      .then(() => {
        try {
          destroyPrimingDialog();
        } catch {
          /* noop */
        }
        primingShownFor = null;
        onClosed();
        return null;
      })
      .catch(() => {
        /* noop */
      });
  };

  primingDialogInstance = f7.dialog.create({
    destroyOnClose: true,
    title: t('push.priming.unsupportedTelegram.title'),
    text: t('push.priming.unsupportedTelegram.body'),
    buttons: [
      {
        text: t('push.priming.unsupportedTelegram.notNow'),
        onClick: () => {
          deferredDestroy();
        },
      },
      {
        text: t('push.priming.unsupportedTelegram.linkTelegram'),
        bold: true,
        onClick: () => {
          const bot = import.meta.env.VITE_TELEGRAM_BOT_USERNAME;
          // A supplier's rail is the bot's DM: the profile's start link records
          // that the bot may write to him (`/start dm_<token>`), where a bare
          // resolve opens the chat and records nothing. A seller's rail is his
          // team group, so he keeps the plain resolve.
          const authStore = useAuthStore();
          const onBotDmRail =
            engagementFor({
              supplierStage: authStore.supplierStage,
              isSupplier: authStore.isSupplier,
              isCollector: authStore.isCollector,
              hasActiveMembership: authStore.hasActiveMembership,
            }).reliableRail === 'bot-dm';
          const startUrl = onBotDmRail ? authStore.telegramDm?.startUrl : null;
          try {
            if (startUrl) {
              adapter.openTelegramChat(startUrl);
            } else if (bot && typeof window !== 'undefined') {
              window.location.href = `tg://resolve?domain=${bot}`;
            } else {
              const target = notificationSettingsPath();
              if (target) f7.views.main?.router?.navigate(target);
            }
          } catch (err) {
            console.warn('[App] Telegram link tap failed:', err);
          }
          deferredDestroy();
        },
      },
    ],
  });
  primingDialogInstance.open();
}
</script>

<style>
.pwa-update-toast {
  --f7-toast-bg-color: #8e5bfe;
  --f7-toast-text-color: #fff;
}
</style>
